// Single source of truth for the numbers a campaign surface displays as *totals*.
//
// Why this file exists (issue #859)
// ---------------------------------
// There are two ledgers for a campaign's progress and they have different lifetimes:
//
//   durable   — hermes_email_campaigns.{recipient_count, emails_sent, emails_failed}
//               incremented once per terminal outcome by increment_campaign_sent_count /
//               increment_campaign_failed_count. Never purged. Verified 2026-07-31:
//               `count(sent_emails) == emails_sent` for every campaign in the table.
//
//   ephemeral — hermes_email_queue_jobs rows. cleanup_old_email_logs() (pg_cron job 64,
//               02:00 daily) runs `DELETE ... WHERE status='sent' AND sent_at < now() - '7 days'`.
//               Rows are a *last-7-days activity window*, not a census.
//
// The campaign UI derived "Sent" from the ephemeral ledger with an all-or-nothing fallback
// (`jobs.length > 0 ? jobCounts.sent : campaign.emails_sent`). While a campaign straddles the
// 7-day boundary the displayed count decays daily; a campaign whose `sent` rows all aged out
// but which retains a `failed` row (never purged) never reaches the fallback and reports 0.
// Measured 2026-07-31: 9 of 51 campaigns undercounted, 2002 delivered shown as 675.
//
// The rule this module enforces: **totals come from the durable ledger; the job window is only
// ever consulted for the skipped-vs-failed breakdown, which callers must label as recent
// activity.** computeCampaignJobCounts (./job-status) stays exactly as it is — it is correct;
// the bug was in how callers used it for totals.
import type { CampaignJobCounts } from './job-status';

/** The durable counters carried on the hermes_email_campaigns row. All nullable in Postgres. */
export interface DurableCampaignCounters {
  recipient_count?: number | null;
  emails_sent?: number | null;
  emails_failed?: number | null;
  emails_pending?: number | null;
}

export interface CampaignTotals {
  /** Enrolled recipients. Durable. */
  recipients: number;
  /** Emails actually delivered. Durable — never a surviving-row count. */
  sent: number;
  /** Real provider failures, excluding suppression skips. */
  failed: number;
  /** Suppression rejections (unsubscribed / bounced / complained), from the job window. */
  skipped: number;
  /** Recipients with no terminal outcome yet. Derived, so the four numbers sum to `recipients`. */
  pending: number;
  /** 0–100, clamped. */
  completionPercentage: number;
  /** How many job rows backed the breakdown. 0 once the window has been purged. */
  windowRows: number;
  /**
   * False when the caller had no settled job window to give us — the query is still in flight,
   * errored, or was never enabled. `windowRows: 0` then means "we don't know", NOT "purged", and
   * no surface may claim anything about the window. The durable totals are unaffected.
   */
  windowKnown: boolean;
}

const atLeastZero = (n: number | null | undefined): number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;

const EMPTY_WINDOW: CampaignJobCounts = { sent: 0, pending: 0, skipped: 0, failed: 0, total: 0 };

/**
 * Resolves the totals a campaign surface should display.
 *
 * @param campaign   the durable counters off the campaign row
 * @param jobCounts  computeCampaignJobCounts(jobs) over whatever queue rows survive. Pass null
 *                   (or omit) while the job query is loading, errored, or disabled — anything
 *                   other than a settled result would be indistinguishable from a purged window.
 */
export function resolveCampaignTotals(
  campaign: DurableCampaignCounters,
  jobCounts?: CampaignJobCounts | null,
): CampaignTotals {
  const window = jobCounts ?? EMPTY_WINDOW;

  const recipients = atLeastZero(campaign.recipient_count);
  const sent = atLeastZero(campaign.emails_sent);

  // Suppression skips are only distinguishable from real failures via the per-job
  // error_message, so this number is 0 once the window is purged — at which point the skips
  // fold back into `failed` below rather than silently vanishing.
  const skipped = atLeastZero(window.skipped);

  // The durable emails_failed counter includes suppression skips, so subtract them back out.
  //
  // Two corrections on top of that, both load-bearing:
  //
  //  - Floor at the failures the surviving rows still show. The row UPDATE and the counter RPC
  //    in email-queue-processor are two sequential non-transactional calls, so the counter can
  //    under-record (observed on 2 of 51 campaigns, 2026-07-31). Never hide a failure the user
  //    can already see.
  //
  //  - Cap it at the room left once sent, skips and *currently pending* rows are accounted for.
  //    emails_failed is only ever incremented, never decremented, but "Retry Failed"
  //    (edge-functions-api.ts retryFailedJobs) flips rows straight back to pending. Without the
  //    cap a retried campaign reports its old failures AND loses the in-flight rows to the
  //    pending floor below — 45 recipients silently vanishing on db09242e. No recipient may be
  //    counted twice.
  const pendingInWindow = atLeastZero(window.pending);
  const roomForFailures = Math.max(0, recipients - sent - skipped - pendingInWindow);
  const failed = Math.max(
    Math.min(atLeastZero(campaign.emails_failed) - skipped, roomForFailures),
    atLeastZero(window.failed),
  );

  // Derived, not read from emails_pending: it keeps sent + skipped + failed + pending ==
  // recipients, so the cards can never disagree with each other. Verified against every active
  // campaign on 2026-07-31 — derived pending matched emails_pending and the live queue exactly.
  const terminal = sent + skipped + failed;
  const pending = Math.max(0, recipients - terminal);

  // Clamped: 2 legacy campaigns have emails_sent + emails_failed > recipient_count.
  const completionPercentage = recipients > 0 ? Math.min(100, (terminal / recipients) * 100) : 0;

  return {
    recipients,
    sent,
    failed,
    skipped,
    pending,
    completionPercentage,
    windowRows: window.total,
    windowKnown: jobCounts != null,
  };
}

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

/**
 * The one sentence a campaign surface should show about its per-recipient list, or null when
 * there is nothing honest to say. Centralised so all five surfaces word it identically and so
 * the "records were removed" claim can only be made where it is actually true.
 *
 * The purge only ever deletes rows with `status='sent'`, so an empty window is evidence of a
 * purge ONLY if something was delivered. A campaign that never sent anything never had rows to
 * lose, and an unsettled query knows nothing at all (#859).
 */
export function describeCampaignWindow(totals: CampaignTotals): string | null {
  const { windowKnown, windowRows, recipients, sent, failed } = totals;
  if (!windowKnown || recipients === 0) return null;

  if (windowRows > 0) {
    if (windowRows >= recipients) return null;
    // The count is always honest; only append the *reason* when a purge could have caused it.
    return sent > 0
      ? `Showing ${windowRows} of ${recipients}. Detailed per-email records are removed 7 days after sending.`
      : `Showing ${windowRows} of ${recipients}.`;
  }

  if (sent > 0) {
    const delivered = Math.min(sent, recipients);
    return (
      "Detailed per-email records for this campaign have been removed (they're cleared 7 days " +
      `after sending). ${delivered} of ${recipients} ${plural(recipients, 'email was', 'emails were')} delivered.`
    );
  }

  if (failed > 0) {
    return `No per-email records for this campaign. Nothing reached its ${recipients} ${plural(recipients, 'recipient', 'recipients')}.`;
  }

  return 'No emails have been queued for this campaign yet.';
}
